import type { JoinMeetingResponse, LobbyEntry, Meeting, MeetingParticipant } from '@cyphex/types/meetings';
import type { JoinMeetingResponseDto, MeetingDto, MeetingParticipantDto } from './contracts';

const DEFAULT_MAX_VIDEO = 9;
const DEFAULT_MAX_PUBLISHERS = 16;

/** `Meeting` (wire) → the flat DTO every meeting component already reads. */
export function toMeetingDto(meeting: Meeting): MeetingDto {
  return {
    id: meeting.id,
    title: meeting.title,
    status: meeting.status,
    organizerId: meeting.organizer.id,
    joinCode: meeting.joinCode ?? '',
    sfuRoom: '',
    actualStart: meeting.actualStart,
    lobbyPolicy: meeting.lobbyPolicy,
    presenterPolicy: meeting.presenterPolicy,
    allowChat: meeting.allowChat,
    allowReactions: meeting.allowReactions,
    allowRaiseHand: meeting.allowRaiseHand,
    allowQa: meeting.allowQa,
    allowRecording: meeting.allowRecording,
    muteOnEntry: meeting.muteOnEntry,
    cameraOffOnEntry: meeting.cameraOffOnEntry,
    isLocked: meeting.isLocked,
    allowAttendeeUnmute: meeting.allowAttendeeUnmute,
    allowAttendeeVideo: meeting.allowAttendeeVideo,
    allowAttendeeShare: meeting.allowAttendeeShare,
    maxParticipants: meeting.maxParticipants,
    participantCount: meeting.participantCount,
    recording: 'off',
    recordingStartedBy: null,
  };
}

export function toParticipantDto(participant: MeetingParticipant): MeetingParticipantDto {
  return {
    id: participant.id,
    userId: participant.user?.id ?? participant.sfuIdentity,
    displayName: participant.user?.displayName ?? participant.guestName ?? 'Guest',
    ...(participant.user?.avatarUrl == null ? {} : { avatarUrl: participant.user.avatarUrl }),
    role: participant.role,
    state: participant.state === 'joined' ? 'joined' : participant.state,
    isAudioEnabled: participant.isAudioEnabled,
    isVideoEnabled: participant.isVideoEnabled,
    isScreenSharing: participant.isScreenSharing,
    isHandRaised: participant.isHandRaised,
    handRaisedAt: participant.handRaisedAt,
    isSpotlighted: participant.isSpotlighted,
    forceMuted: participant.forceMuted,
    cameraDisabledByHost: participant.cameraDisabledByHost,
    shareDisabledByHost: participant.shareDisabledByHost,
    joinedAt: participant.joinedAt,
  };
}

/** A lobby entry has no media state yet — it renders as a waiting row only. */
export function lobbyEntryToDto(entry: LobbyEntry): MeetingParticipantDto {
  return {
    id: entry.participantId,
    userId: entry.user?.id ?? entry.participantId,
    displayName: entry.user?.displayName ?? entry.guestName ?? 'Guest',
    ...(entry.user?.avatarUrl == null ? {} : { avatarUrl: entry.user.avatarUrl }),
    role: 'attendee',
    state: 'waiting',
    isAudioEnabled: false,
    isVideoEnabled: false,
    isScreenSharing: false,
    isHandRaised: false,
    handRaisedAt: null,
    isSpotlighted: false,
    forceMuted: false,
    cameraDisabledByHost: false,
    shareDisabledByHost: false,
    joinedAt: null,
  };
}

/**
 * The join response is where the media layer gets everything it needs. When the
 * caller lands in the lobby the API deliberately withholds `sfu` — no token is
 * minted for someone who has not been admitted, which is what makes the lobby a
 * real boundary rather than a UI convention.
 */
export function toJoinDto(response: JoinMeetingResponse): JoinMeetingResponseDto {
  const sfu = response.sfu;
  return {
    url: sfu?.url ?? '',
    token: sfu?.token ?? '',
    room: sfu?.room ?? '',
    identity: sfu?.identity ?? '',
    // The participant ROW id. The meeting topic names people by it —
    // `meeting.reaction` carries it — and without it a client cannot tell its
    // own echo from somebody else's event.
    participantId: response.participantId,
    profile: response.policy.profile,
    roomLimits: {
      maxParticipants: response.meeting.maxParticipants,
      maxVideoSubscriptions: response.policy.maxVideoSubscriptions ?? DEFAULT_MAX_VIDEO,
      maxPublishers: DEFAULT_MAX_PUBLISHERS,
    },
    waiting: response.state === 'waiting',
  };
}
